// src/utils/authUtils.js
import { jwtDecode } from 'jwt-decode';
import { AUTH_CONFIG, STORAGE_KEYS, ERROR_MESSAGES } from '../config/constants';

/**
 * Retrieves the authentication token from local storage.
 * @returns {string|null} The token, or null if not found.
 */
export const getToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error getting token from local storage:', error);
    }
    return null;
  }
};

/**
 * Stores the authentication token in local storage.
 * @param {string} token The JWT token to store.
 */
export const setToken = (token) => {
  try {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error setting token in local storage:', error);
    }
  }
};

/**
 * Removes the authentication token from local storage.
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error removing token from local storage:', error);
    }
  }
};

/**
 * Validates a JWT token and extracts its claims.
 * @param {string} token - The JWT token to validate
 * @returns {Object} An object with { valid, decoded, error }
 */
export const validateToken = (token) => {
  if (AUTH_CONFIG.DEBUG.ENABLED && AUTH_CONFIG.DEBUG.LOG_TOKEN_VALIDATION) {
    console.log('Validating token:', token ? `${token.substring(0, 20)}...` : 'null');
  }
  
  if (!token) {
    // Don't log anything - it's normal for users to not be logged in
    return { valid: false, decoded: null, error: ERROR_MESSAGES.AUTH.TOKEN_MISSING };
  }

  try {
    // Check if it's a JWT token (contains dots)
    if (token.includes('.')) {
      console.log('Token is a JWT, attempting to decode');
      // Decode the JWT token
      try {
        const decoded = jwtDecode(token);

        // Check if token has expired
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp && decoded.exp < currentTime) {
          console.error('[EXPIRED] JWT token expired');
          return { 
            valid: false, 
            decoded, 
            error: ERROR_MESSAGES.AUTH.TOKEN_EXPIRED,
            expired: true
          };
        }
        
        // Check if token is about to expire (within buffer time)
        // For long-lived tokens (>30 days), don't set expiringSoon flag
        const timeUntilExpiry = decoded.exp ? (decoded.exp - currentTime) * 1000 : 0;
        const isLongLivedToken = timeUntilExpiry > 30 * 24 * 60 * 60 * 1000; // 30 days
        const isExpiringSoon = decoded.exp && 
          !isLongLivedToken && 
          timeUntilExpiry < AUTH_CONFIG.TOKEN_EXPIRY_BUFFER;
        
        if (isLongLivedToken) {
          console.log('Token is long-lived (>30 days), skipping expiry warning');
        }
        
        // Log token validation in debug mode
        if (AUTH_CONFIG.DEBUG.ENABLED) {
          console.log('Token valid, expires in:', decoded.exp ? `${Math.floor((decoded.exp - currentTime) / 60)} minutes` : 'unknown');
        }
        
        return { 
          valid: true, 
          decoded, 
          expiringSoon: isExpiringSoon 
        };
      } catch (jwtError) {
        console.error('[ERROR] JWT decode error:', jwtError);
        return { 
          valid: false, 
          decoded: null, 
          error: 'Invalid JWT format: ' + jwtError.message
        };
      }
    } else {
      console.log('Token is not a JWT, treating as simple token');
      // Handle simple string tokens (from our backend OTP system)
      if (token.length >= 32) { // Minimum length check for security
        console.log('Simple token validated (length check passed)');
        // Log token validation
        if (AUTH_CONFIG.DEBUG.ENABLED) {
          console.log('Simple token is valid');
        }
        // For simple tokens, create a mock decoded object
        return { 
          valid: true, 
          decoded: {
            sub: 'user',
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours from now
          }, 
          expiringSoon: false 
        };
      } else {
        return { 
          valid: false, 
          decoded: null, 
          error: 'Token too short or invalid format'
        };
      }
    }
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Token validation error:', error);
    }
    return { 
      valid: false, 
      decoded: null, 
      error: ERROR_MESSAGES.AUTH.TOKEN_INVALID
    };
  }
};

/**
 * Gets the user data from local storage.
 * @returns {Object|null} The user data object or null if not found/invalid
 */
export const getUserData = () => {
  try {
    // Try to get from the current key
    let userData = localStorage.getItem(STORAGE_KEYS.USER);
    
    // If not found, try legacy key
    if (!userData && STORAGE_KEYS.USER !== 'userData') {
      userData = localStorage.getItem('userData');
      
      // If found in legacy storage, migrate it
      if (userData) {
        localStorage.setItem(STORAGE_KEYS.USER, userData);
        localStorage.removeItem('userData');
      }
    }
    
    if (!userData) {
      return null;
    }
    
    return JSON.parse(userData);
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error getting user data from local storage:', error);
    }
    return null;
  }
};

/**
 * Sets the user data in local storage.
 * @param {Object} userData - The user data to store
 * @returns {boolean} Whether the operation was successful
 */
export const setUserData = (userData) => {
  if (!userData) return false;
  
  try {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    return true;
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error setting user data in local storage:', error);
    }
    return false;
  }
};

/**
 * Validates user data integrity and repairs if needed.
 * @param {Object} userData - The user data to validate
 * @param {string} userType - Optional user type to enforce
 * @returns {Object} The validated (and possibly repaired) user data
 */
export const validateUserData = (userData, userType = null) => {
  if (!userData) return null;
  
  // Create a copy to avoid mutating the original
  const validatedUser = { ...userData };
  let wasRepaired = false;
  
  // Check for required fields
  AUTH_CONFIG.VALIDATION.REQUIRED_USER_FIELDS.forEach(field => {
    if (validatedUser[field] === undefined || validatedUser[field] === null) {
      wasRepaired = true;
      
      // For userType, use the provided value or a default
      if (field === 'userType') {
        validatedUser.userType = userType || 'candidate';
      }
    }
  });
  
  // Ensure profileCompleted is a boolean
  if (typeof validatedUser.profileCompleted !== 'boolean') {
    validatedUser.profileCompleted = !!validatedUser.profileCompleted;
    wasRepaired = true;
  }
  
  // If userType is explicitly provided, enforce it
  if (userType && validatedUser.userType !== userType) {
    validatedUser.userType = userType;
    wasRepaired = true;
  }
  
  // If repairs were made and the config allows it, save the repaired data
  if (wasRepaired && AUTH_CONFIG.VALIDATION.REPAIR_CORRUPTED_DATA) {
    setUserData(validatedUser);
    
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.warn('User data was repaired:', { 
        before: userData, 
        after: validatedUser 
      });
    }
  }
  
  return validatedUser;
};

/**
 * Clears all authentication data from storage.
 */
export const clearAuthData = () => {
  try {
    // Remove token
    removeToken();
    
    // Remove user data
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('userData'); // Legacy
    
    // Remove session storage items
    sessionStorage.removeItem('userType');
    sessionStorage.removeItem('profileCompleted');
    
    // Remove auth state tracking
    localStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
    localStorage.removeItem(STORAGE_KEYS.AUTH_ERRORS);
    
    return true;
  } catch (error) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error clearing auth data:', error);
    }
    return false;
  }
};

/**
 * Logs authentication state for debugging.
 * @param {string} action - The action being performed
 * @param {Object} data - Additional data to log
 */
export const logAuthState = (action, data = {}) => {
  if (!AUTH_CONFIG.DEBUG.ENABLED || !AUTH_CONFIG.DEBUG.LOG_AUTH_STATE) {
    return;
  }
  
  // Skip logging these common "no auth" events to reduce console noise
  const skipActions = ['INIT_TOKEN_INVALID', 'INIT_START'];
  if (skipActions.includes(action) && !data.error) {
    return;
  }
  
  const timestamp = new Date().toISOString();
  const state = {
    action,
    timestamp,
    ...data
  };

  // Store in local storage for persistence
  try {
    const authStateLog = JSON.parse(localStorage.getItem(STORAGE_KEYS.AUTH_STATE) || '[]');
    authStateLog.push(state);
    
    // Keep only the last 20 entries
    if (authStateLog.length > 20) {
      authStateLog.shift();
    }
    
    localStorage.setItem(STORAGE_KEYS.AUTH_STATE, JSON.stringify(authStateLog));
  } catch (error) {
    // Silent error - logging should not break functionality
  }
};

/**
 * Determines user type from token or stored data
 * @param {Object} decoded - Decoded JWT token
 * @returns {string} - 'candidate' or 'employer'
 */
export const determineUserType = (decoded) => {
  // First try to get from token
  if (decoded) {
    // Check for explicit user type field
    if (decoded.userType) {
      return decoded.userType;
    }
    
    // Check for role field that might indicate user type
    if (decoded.role) {
      if (decoded.role === 'candidate' || decoded.role === 'employer') {
        return decoded.role;
      }
      // Handle other role naming conventions
      if (decoded.role.includes('candidate')) return 'candidate';
      if (decoded.role.includes('recruiter')) return 'recruiter';
      if (decoded.role.includes('employer')) return 'employer';
    }
  }
  
  // Try to get from localStorage
  try {
    const userData = getUserData();
    if (userData && userData.userType) {
      return userData.userType;
    }
  } catch (e) {
    if (AUTH_CONFIG.DEBUG.ENABLED) {
      console.error('Error determining user type from localStorage:', e);
    }
  }
  
  // Default to candidate if we can't determine
  return 'candidate';
};
